import {
  computed,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConfigurationGateway } from './configuration.gateway';
import {
  ConfigurationSettingKey,
  DEFAULT_CONFIGURATION,
  findConfigurationByKeyAndChannel,
  IConfiguration,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { EConfigurationChannel } from '@web/dtos';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationStore {
  private readonly gateway: ConfigurationGateway = new ConfigurationGateway();
  private readonly licenseKey: WritableSignal<string | undefined> = signal<
    string | undefined
  >(undefined);

  readonly #configurationResource: ResourceRef<
    IApiResponse<IConfiguration[]> | undefined
  > = resource({
    loader: (): Promise<IApiResponse<IConfiguration[]>> => {
      return firstValueFrom(this.gateway.get());
    },
  });

  readonly #licenseConfigurationResource: ResourceRef<
    IApiResponse<IConfiguration[]> | undefined
  > = resource({
    params: this.licenseKey,
    loader: ({
      params,
    }: ResourceLoaderParams<string | undefined>): Promise<
      IApiResponse<IConfiguration[]>
    > =>
      params
        ? firstValueFrom(this.gateway.getByLicenseKey(params))
        : Promise.resolve({ data: [] }),
  });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#configurationResource.isLoading()
  );
  readonly configuration: Signal<IConfiguration[]> = computed(
    (): IConfiguration[] =>
      this.#configurationResource.hasValue()
        ? (this.#configurationResource.value()?.data ?? DEFAULT_CONFIGURATION)
        : DEFAULT_CONFIGURATION
  );
  readonly isLicenseConfigurationLoading: Signal<boolean> = computed(
    (): boolean => this.#licenseConfigurationResource.isLoading()
  );
  readonly licenseConfiguration: Signal<IConfiguration[]> = computed(
    (): IConfiguration[] =>
      this.#licenseConfigurationResource.hasValue()
        ? (this.#licenseConfigurationResource.value()?.data ?? [])
        : []
  );

  loadForLicense(licenseKey: string): void {
    this.licenseKey.set(licenseKey);
  }

  reloadForLicense(): void {
    this.#licenseConfigurationResource.reload();
  }

  async saveForLicense(configuration: IConfiguration[]): Promise<void> {
    await firstValueFrom(this.gateway.save(configuration));
    this.#licenseConfigurationResource.update(
      (): IApiResponse<IConfiguration[]> => ({ data: configuration })
    );
    this.reload();
  }

  reload(): void {
    this.#configurationResource.reload();
  }

  updateConfiguration(query: IConfiguration[]): void {
    this.#configurationResource.update((): IApiResponse<IConfiguration[]> => {
      return {
        data: query,
      };
    });
  }

  save(query: IConfiguration[]): void {
    firstValueFrom(this.gateway.save(query)).then(
      (result: IApiResponse<IConfiguration[]>): void => {
        if (result && result.data) {
          this.updateConfiguration(result.data);
        }
      }
    );
  }

  getConfigurationByKey(
    settingKey: ConfigurationSettingKey,
    channel: EConfigurationChannel = EConfigurationChannel.Web
  ): IConfiguration | undefined {
    return findConfigurationByKeyAndChannel(
      this.configuration(),
      settingKey,
      channel
    );
  }

  getBooleanConfiguration(settingKey: ConfigurationSettingKey): boolean {
    return this.getConfigurationByKey(settingKey)?.value === 'true';
  }

  getNumberConfiguration(settingKey: ConfigurationSettingKey): number {
    return Number(this.getConfigurationByKey(settingKey)?.value ?? 0);
  }

  getStringConfiguration(settingKey: ConfigurationSettingKey): string {
    return this.getConfigurationByKey(settingKey)?.value ?? '';
  }

  getMultiSelectConfiguration<T extends string>(
    settingKey: ConfigurationSettingKey,
    channel: EConfigurationChannel = EConfigurationChannel.Web
  ): T[] {
    const value: string =
      this.getConfigurationByKey(settingKey, channel)?.value ?? '[]';

    try {
      const parsedValue: unknown = JSON.parse(value);
      return Array.isArray(parsedValue)
        ? parsedValue.filter((item: unknown): item is T =>
            typeof item === 'string'
          )
        : [];
    } catch {
      return [];
    }
  }

  hasMultiSelectConfigurationValue<T extends string>(
    settingKey: ConfigurationSettingKey,
    value: T,
    channel: EConfigurationChannel = EConfigurationChannel.Web
  ): boolean {
    return this.getMultiSelectConfiguration<T>(settingKey, channel).includes(
      value
    );
  }

  getEnumConfiguration<T extends string>(
    settingKey: ConfigurationSettingKey,
    enumObject: Record<string, T>
  ): T {
    const value: string = this.getStringConfiguration(settingKey);
    const enumValues: T[] = Object.values(enumObject);

    if (enumValues.includes(value as T)) {
      return value as T;
    }

    throw new Error(
      `Invalid enum configuration "${settingKey}": "${value}" is not one of ${enumValues.join(', ')}`
    );
  }
}
